'use client';

import { Sun, Factory, Leaf, ArrowUpRight } from 'lucide-react';

const KPIS = [
  { icon: Sun, value: '12 MW', label: 'Capacité solaire' },
  { icon: Factory, value: '850 kg', label: 'Or raffiné / an' },
  { icon: Leaf, value: '100%', label: 'Énergie verte' },
];

export default function UsineSolaire() {
  return (
    <section
      id="usine-solaire"
      className="relative py-24 sm:py-32 bg-[#0a0a0f] border-t border-[#D4AF37]/10 overflow-hidden"
    >
      {/* Background gold radial */}
      <div
        className="absolute -top-1/3 -left-1/4 w-[700px] h-[700px] rounded-full opacity-[0.08] pointer-events-none"
        style={{ background: 'radial-gradient(circle, #D4AF37 0%, transparent 70%)' }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="block h-px w-10 bg-[#D4AF37]" />
            <span className="eyebrow">L'Entreprise Sœur</span>
            <span className="block h-px w-10 bg-[#D4AF37]" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4">
            <span className="text-gold-gradient">Usine Solaire Or</span>
            <br />
            <span className="text-[#F4E8C1] text-2xl sm:text-3xl lg:text-4xl font-light tracking-[0.08em]">
              du Sénégal
            </span>
          </h2>
          <p className="mt-5 max-w-2xl mx-auto text-[#F4E8C1]/65 font-light leading-relaxed">
            Une vision pionnière née à Dakar : allier l'énergie solaire et
            l'orfèvrerie pour produire un or éthique, solaire, panafricain.
            Une entreprise du groupe Smartest Capital, dirigée par Matar Sakho.
          </p>
        </div>

        {/* Main two-column block — Matar Sakho positioned on the right (1er à droite) */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-stretch mb-16">
          {/* LEFT — Usine image + caption + KPIs */}
          <div className="flex flex-col">
            {/* Usine image */}
            <div className="relative aspect-[16/10] overflow-hidden border-gold-thin glow-gold">
              <img
                src="/images/usine-solaire-or.png"
                alt="Usine Solaire Or du Sénégal — panneaux solaires et bâtiment doré au coucher du soleil"
                className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out hover:scale-105"
                style={{ filter: 'brightness(0.85) saturate(1.15)' }}
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    'linear-gradient(180deg, transparent 50%, rgba(10,10,15,0.85) 100%)',
                }}
              />
              {/* Caption */}
              <div className="absolute bottom-5 left-5 right-5">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-1">
                  Dakar · Sénégal
                </div>
                <div className="font-display text-lg sm:text-xl text-[#F4E8C1] font-semibold tracking-wide">
                  Complexe Solaire Doré
                </div>
              </div>
            </div>

            {/* KPIs row */}
            <div className="grid grid-cols-3 gap-3 mt-5">
              {KPIS.map((kpi) => (
                <div
                  key={kpi.label}
                  className="bg-[#12121a] border border-[#D4AF37]/20 p-4 text-center hover:border-[#D4AF37]/45 transition-colors"
                >
                  <kpi.icon
                    size={18}
                    className="text-[#D4AF37] mx-auto mb-2"
                  />
                  <div className="font-display text-base sm:text-lg text-gold-gradient font-bold">
                    {kpi.value}
                  </div>
                  <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-[#F4E8C1]/55 mt-1">
                    {kpi.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — Matar Sakho portrait (1er à droite) */}
          <div className="relative flex flex-col">
            {/* Portrait card */}
            <div className="relative flex-1 overflow-hidden border-gold-thin group">
              <img
                src="/images/matar-sakho.png"
                alt="Matar Sakho — Directeur de l'Usine Solaire Or du Sénégal"
                className="w-full h-full object-cover min-h-[420px] transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                style={{ filter: 'brightness(0.92) saturate(1.05)' }}
              />
              {/* Top-right badge */}
              <div className="absolute top-4 right-4 bg-gold-gradient text-[#0a0a0f] text-[9px] uppercase tracking-[0.25em] font-bold px-2.5 py-1">
                Directeur Général
              </div>
              {/* Bottom caption */}
              <div
                className="absolute bottom-0 left-0 right-0 p-6"
                style={{
                  background:
                    'linear-gradient(180deg, transparent 0%, rgba(10,10,15,0.95) 60%)',
                }}
              >
                <div className="font-display text-xl sm:text-2xl font-semibold text-[#F4E8C1] tracking-wide">
                  Matar Sakho
                </div>
                <div className="text-[#D4AF37] text-xs sm:text-sm mt-1 italic font-serif">
                  Fondateur & Directeur Général
                </div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#F4E8C1]/55 mt-3">
                  Dakar · Sénégal
                </div>
              </div>
            </div>

            {/* Decorative corner accents */}
            <div className="absolute -top-3 -right-3 w-16 h-16 border-t border-r border-[#D4AF37]/60 pointer-events-none" />
            <div className="absolute -bottom-3 -left-3 w-16 h-16 border-b border-l border-[#D4AF37]/60 pointer-events-none" />
          </div>
        </div>

        {/* Narrative — full width */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <span className="block h-px w-10 bg-[#D4AF37]" />
              <span className="eyebrow">La Vision</span>
            </div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold leading-tight mb-5">
              <span className="text-[#F4E8C1]">Un or né du soleil,</span>{' '}
              <span className="text-gold-gradient">forgé en Afrique.</span>
            </h3>
            <div className="space-y-4 text-[#F4E8C1]/70 leading-relaxed font-light">
              <p>
                Sous l'impulsion de{' '}
                <span className="text-[#D4AF37]">Matar Sakho</span>, l'Usine
                Solaire Or du Sénégal incarne une ambition rare : faire de
                l'industrie aurifère un acte de responsabilité écologique et
                de souveraineté panafricaine. Douze mégawatts de capacité
                solaire alimentent un complexe de raffinage à la pointe de
                l'innovation, où chaque lingot est aussi propre que précieux.
              </p>
              <p>
                Ce n'est pas qu'une usine. C'est une déclaration : que le
                Sénégal, que l'Afrique, peuvent produire l'or du futur —
                celui qui respecte la terre, celle qui l'a porté. Une
                philosophie qui résonne avec la maison MATAR STYLE CUIR :
                extraire la beauté sans jamais épuiser la matière.
              </p>
            </div>
          </div>

          {/* Pillars */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <span className="eyebrow">Les Piliers</span>
            </div>
            {[
              {
                title: 'Souveraineté énergétique',
                text: '100% énergie solaire, autonomie totale du réseau.',
              },
              {
                title: 'Or éthique & traçable',
                text: 'Chaîne de certification complète, du minerai au lingot.',
              },
              {
                title: 'Emploi panafricain',
                text: '320 collaborateurs locaux formés aux métiers de l\'or.',
              },
            ].map((p, i) => (
              <div
                key={i}
                className="border-l-2 border-[#D4AF37]/40 pl-4 py-1 hover:border-[#D4AF37] transition-colors"
              >
                <div className="font-display text-sm font-semibold text-[#F4E8C1] tracking-wide">
                  {p.title}
                </div>
                <div className="text-xs text-[#F4E8C1]/55 mt-1 leading-relaxed font-light">
                  {p.text}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-14 flex justify-center">
          <a
            href="https://smartest-capital.org"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 px-8 py-4 border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] transition-all duration-500"
          >
            <span className="text-xs uppercase tracking-[0.25em] text-[#F4E8C1] group-hover:text-[#D4AF37] transition-colors">
              Découvrir le groupe Smartest Capital
            </span>
            <ArrowUpRight
              size={16}
              className="text-[#D4AF37] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300"
            />
          </a>
        </div>
      </div>
    </section>
  );
}
